/**
 * Report Preview Panel
 * Displays report plan before creation
 */

import React from 'react';
import { Button, Text, Badge } from '@fluentui/react-components';
import { CheckmarkCircleRegular } from '@fluentui/react-icons';
import type { ReportPlan } from '@excel-ai-studio/shared';
import { INSIGHT_TYPES } from '@excel-ai-studio/shared';
import '../common/PreviewPanel.css';
import './ReportPreviewPanel.css';

interface ReportPreviewPanelProps {
  readonly plan: ReportPlan;
  readonly onGenerate: () => void;
  readonly isGenerating: boolean;
}

export const ReportPreviewPanel: React.FC<ReportPreviewPanelProps> = ({
  plan,
  onGenerate,
  isGenerating,
}) => {
  return (
    <div className="preview-panel report-preview-panel">
      {/* Header */}
      <div className="preview-header">
        <div className="preview-title-section">
          <Text weight="semibold" size={400}>
            {plan.title}
          </Text>
          <Badge
            appearance="outline"
            color={plan.qualityScore > 0.8 ? 'success' : plan.qualityScore > 0.6 ? 'warning' : 'danger'}
          >
            Score: {(plan.qualityScore * 100).toFixed(0)}%
          </Badge>
        </div>
        <Text size={300} className="preview-description">
          {plan.description}
        </Text>
      </div>

      {/* Dataset Info */}
      <div className="preview-section">
        <Text weight="semibold" size={300}>
          Informations Dataset
        </Text>
        <div className="info-grid">
          <div className="info-item">
            <Text size={200} className="info-label">Type</Text>
            <Text size={300}>{plan.datasetType}</Text>
          </div>
          <div className="info-item">
            <Text size={200} className="info-label">Lignes</Text>
            <Text size={300}>{plan.rowCount.toLocaleString()}</Text>
          </div>
          <div className="info-item">
            <Text size={200} className="info-label">Colonnes</Text>
            <Text size={300}>{plan.columnCount}</Text>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="preview-section">
        <Text weight="semibold" size={300}>
          Résumé Analytique
        </Text>
        <Text size={300} className="summary-text">
          {plan.summary}
        </Text>
      </div>

      {/* Insights */}
      {plan.insights.length > 0 && (
        <div className="preview-section">
          <Text weight="semibold" size={300}>
            Points Clés ({plan.insights.length})
          </Text>
          <div className="insights-list">
            {plan.insights.map((insight, idx) => {
              const insightType = INSIGHT_TYPES[insight.type];
              return (
                <div key={idx} className="insight-item">
                  <div className="insight-header">
                    <span className="insight-icon">{insightType.icon}</span>
                    <Text weight="semibold" size={300}>
                      {insight.title}
                    </Text>
                  </div>
                  <Text size={200} className="insight-description">
                    {insight.description}
                  </Text>
                  <div className="insight-confidence">
                    <Text size={200}>
                      Confiance: {(insight.confidence * 100).toFixed(0)}%
                    </Text>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Charts */}
      {plan.charts.length > 0 && (
        <div className="preview-section">
          <Text weight="semibold" size={300}>
            Graphiques ({plan.charts.length})
          </Text>
          <div className="charts-list">
            {plan.charts.map((chart, idx) => (
              <div key={idx} className="chart-item">
                <div className="chart-header">
                  <Text weight="semibold" size={300}>
                    {chart.title}
                  </Text>
                  <Badge appearance="outline">
                    {chart.type}
                  </Badge>
                </div>
                <div className="chart-info">
                  <Text size={200}>
                    X: {chart.xColumn} | Y: {chart.yColumn}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sections */}
      {plan.sections.length > 0 && (
        <div className="preview-section">
          <Text weight="semibold" size={300}>
            Sections du Rapport ({plan.sections.length})
          </Text>
          <div className="sections-list">
            {plan.sections.map((section, idx) => (
              <div key={idx} className="section-item">
                <CheckmarkCircleRegular className="section-icon" />
                <Text size={300}>
                  {section.title || section.type}
                </Text>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Button */}
      <div className="preview-actions">
        <Button
          appearance="primary"
          size="large"
          onClick={onGenerate}
          disabled={isGenerating}
        >
          {isGenerating ? 'Création en cours...' : 'Créer le Rapport'}
        </Button>
      </div>
    </div>
  );
};
